import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Badge, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import apiService from '../services/api';

const FiscalEntities = () => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [error, setError] = useState('');
    const [fiscalEntities, setFiscalEntities] = useState([]);
    const [loadingFiscalEntities, setLoadingFiscalEntities] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchName, setSearchName] = useState('');
    const [searchErpId, setSearchErpId] = useState('');
    const [searchTaxId, setSearchTaxId] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingFiscalEntity, setEditingFiscalEntity] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        erp_id: '',
        tax_id: '',
        is_default: false
    });
    const [erpList, setErpList] = useState([]);
    const [loadingErpList, setLoadingErpList] = useState(false);
    const [selectedErpId, setSelectedErpId] = useState('');

    useEffect(() => {
        loadOrganizations();
    }, []);

    useEffect(() => {
        if (selectedOrganization) {
            loadFiscalEntities(selectedOrganization.id);
        } else {
            setFiscalEntities([]);
        }
    }, [selectedOrganization]);

    const loadOrganizations = async () => {
        setLoadingOrganizations(true);
        setError('');

        try {
            const response = await apiService.getOrganizations(1, 1000, '');
            if (response.success) {
                const activeOrgs = (response.data || []).filter(
                    org => (org.subscription_status || '').toLowerCase() === 'active'
                );
                setOrganizations(activeOrgs);
            } else {
                setError(response.error || 'Failed to load organizations');
            }
        } catch (err) {
            console.error('Error loading organizations:', err);
            setError('Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    const loadFiscalEntities = async (organizationId) => {
        setLoadingFiscalEntities(true);
        setError('');

        try {
            const response = await apiService.getFiscalEntitiesByOrganization(organizationId);
            if (response.success) {
                setFiscalEntities(response.data || []);
            } else {
                setError(response.error || 'Failed to load fiscal entities');
                setFiscalEntities([]);
            }
        } catch (err) {
            console.error('Error loading fiscal entities:', err);
            setError('Failed to load fiscal entities');
            setFiscalEntities([]);
        } finally {
            setLoadingFiscalEntities(false);
        }
    };

    const handleOrganizationChange = (e) => {
        const orgId = e.target.value;
        if (orgId) {
            const org = organizations.find(o => o.id === parseInt(orgId));
            setSelectedOrganization(org);
        } else {
            setSelectedOrganization(null);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredAndSortedFiscalEntities = () => {
        let filtered = fiscalEntities.filter(fe => {
            const nameMatch = !searchName ||
                (fe.name && fe.name.toLowerCase().includes(searchName.toLowerCase()));
            const erpMatch = !searchErpId ||
                (fe.erp_id && fe.erp_id.toLowerCase().includes(searchErpId.toLowerCase()));
            const taxMatch = !searchTaxId ||
                (fe.tax_id && fe.tax_id.toLowerCase().includes(searchTaxId.toLowerCase()));
            return nameMatch && erpMatch && taxMatch;
        });

        if (!sortConfig.key) return filtered;

        const sorted = [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortConfig.direction === 'asc' ?
            <i className="bi bi-arrow-up ms-1"></i> :
            <i className="bi bi-arrow-down ms-1"></i>;
    };

    const loadErpList = async () => {
        setLoadingErpList(true);
        try {
            const response = await apiService.getFiscalEntityAvailableErpList();
            if (response.success) {
                setErpList(response.data || []);
            } else {
                setError(response.error || 'Failed to load fiscal entity ERP list');
                setErpList([]);
            }
        } catch (err) {
            console.error('Error loading fiscal entity ERP list:', err);
            setError('Failed to load fiscal entity ERP list');
            setErpList([]);
        } finally {
            setLoadingErpList(false);
        }
    };

    const handleShowModal = (fiscalEntity = null) => {
        if (fiscalEntity) {
            setEditingFiscalEntity(fiscalEntity);
            setFormData({
                name: fiscalEntity.name || '',
                erp_id: fiscalEntity.erp_id || '',
                tax_id: fiscalEntity.tax_id || '',
                is_default: fiscalEntity.is_default || false
            });
        } else {
            setEditingFiscalEntity(null);
            setFormData({
                name: '',
                erp_id: '',
                tax_id: '',
                is_default: false
            });
            setSelectedErpId('');
            loadErpList();
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingFiscalEntity(null);
        setFormData({
            name: '',
            erp_id: '',
            tax_id: '',
            is_default: false
        });
        setSelectedErpId('');
        setErpList([]);
        setError('');
    };

    const handleErpSelectChange = (e) => {
        const erpId = e.target.value;
        setSelectedErpId(erpId);
        const entry = erpList.find(item => String(item.code_client) === String(erpId));
        if (entry) {
            setFormData(prev => ({
                ...prev,
                name: entry.ref || '',
                erp_id: entry.code_client || '',
                tax_id: entry.idprof1 || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                name: '',
                erp_id: '',
                tax_id: ''
            }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }
        if (!formData.erp_id.trim()) {
            setError('ERP ID is required');
            return;
        }
        if (!formData.tax_id.trim()) {
            setError('Tax ID is required');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const fiscalEntityData = {
                organization_id: selectedOrganization.id,
                name: formData.name.trim(),
                erp_id: formData.erp_id.trim(),
                tax_id: formData.tax_id.trim(),
                is_default: formData.is_default
            };

            let response;
            if (editingFiscalEntity) {
                response = await apiService.updateFiscalEntity(editingFiscalEntity.id, fiscalEntityData);
            } else {
                response = await apiService.createFiscalEntity(fiscalEntityData);
            }

            if (response.success) {
                handleCloseModal();
                await loadFiscalEntities(selectedOrganization.id);
            } else {
                setError(response.error || 'Failed to save fiscal entity');
            }
        } catch (err) {
            console.error('Error saving fiscal entity:', err);
            setError('Failed to save fiscal entity');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <i className="bi bi-bank me-2"></i>
                        Fiscal Entities
                    </h2>
                    <p className="text-muted">Manage fiscal entities for each organization</p>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Organization Selection */}
            <Row className="mb-4">
                <Col lg={12}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">
                                            <i className="bi bi-building me-2"></i>
                                            Select Organization *
                                        </Form.Label>
                                        {loadingOrganizations ? (
                                            <div className="text-center py-3">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Loading organizations...
                                            </div>
                                        ) : (
                                            <>
                                                <Form.Select
                                                    value={selectedOrganization?.id || ''}
                                                    onChange={handleOrganizationChange}
                                                    disabled={organizations.length === 0}
                                                >
                                                    <option value="">-- Select an organization --</option>
                                                    {organizations.map(org => (
                                                        <option key={org.id} value={org.id}>
                                                            {org.name} ({org.code})
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {organizations.length === 0 && (
                                                    <Form.Text className="text-danger">
                                                        No active organizations available
                                                    </Form.Text>
                                                )}
                                            </>
                                        )}
                                    </Form.Group>
                                </Col>
                                {selectedOrganization && (
                                    <>
                                        <Col md={5}>
                                            <div className="p-3 bg-light rounded">
                                                <small className="text-muted d-block mb-1">Selected Organization:</small>
                                                <div className="fw-bold">{selectedOrganization.name}</div>
                                                <div className="text-muted">
                                                    <small>Code: <code>{selectedOrganization.code}</code></small>
                                                    {selectedOrganization.erp_id && (
                                                        <small className="ms-3">ERP ID: {selectedOrganization.erp_id}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <Button
                                                variant="primary"
                                                className="w-100"
                                                onClick={() => handleShowModal()}
                                            >
                                                <i className="bi bi-plus-circle me-2"></i>
                                                Add New Fiscal Entity
                                            </Button>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {!selectedOrganization ? (
                <Row>
                    <Col lg={12}>
                        <Card className="shadow-sm">
                            <Card.Body>
                                <div className="text-center py-5">
                                    <i className="bi bi-building" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                                    <h4 className="mt-3 text-muted">No Organization Selected</h4>
                                    <p className="text-muted">
                                        Please select an organization from the dropdown above to view and manage its fiscal entities.
                                    </p>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col lg={12}>
                        <Card className="shadow-sm">
                            <Card.Header>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="bi bi-bank me-2"></i>
                                        Fiscal Entities
                                    </h5>
                                    {!loadingFiscalEntities && fiscalEntities.length > 0 && (
                                        <Badge bg="info">
                                            {getFilteredAndSortedFiscalEntities().length} / {fiscalEntities.length} entit{fiscalEntities.length !== 1 ? 'ies' : 'y'}
                                        </Badge>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {loadingFiscalEntities ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                        <p className="mt-3 text-muted">Loading fiscal entities...</p>
                                    </div>
                                ) : fiscalEntities.length === 0 ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="bi bi-bank display-1"></i>
                                        <div className="mt-2">No fiscal entities found for this organization</div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Search Filters */}
                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">
                                                        <i className="bi bi-search me-1"></i>
                                                        Search by Name
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter name..."
                                                        value={searchName}
                                                        onChange={(e) => setSearchName(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">
                                                        <i className="bi bi-search me-1"></i>
                                                        Search by ERP ID
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter ERP ID..."
                                                        value={searchErpId}
                                                        onChange={(e) => setSearchErpId(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-bold">
                                                        <i className="bi bi-search me-1"></i>
                                                        Search by Tax ID
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter Tax ID..."
                                                        value={searchTaxId}
                                                        onChange={(e) => setSearchTaxId(e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        {(searchName || searchErpId || searchTaxId) && (
                                            <div className="mb-3">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSearchName('');
                                                        setSearchErpId('');
                                                        setSearchTaxId('');
                                                    }}
                                                >
                                                    <i className="bi bi-x-circle me-1"></i>
                                                    Clear Filters
                                                </Button>
                                            </div>
                                        )}
                                        {getFilteredAndSortedFiscalEntities().length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <i className="bi bi-search display-4"></i>
                                                <div className="mt-2">No fiscal entities match your search criteria</div>
                                                <Button
                                                    variant="link"
                                                    onClick={() => {
                                                        setSearchName('');
                                                        setSearchErpId('');
                                                        setSearchTaxId('');
                                                    }}
                                                >
                                                    Clear filters
                                                </Button>
                                            </div>
                                        ) : (
                                            <Table striped bordered hover responsive>
                                                <thead className="table-dark">
                                                    <tr>
                                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                                            Name {getSortIcon('name')}
                                                        </th>
                                                        <th onClick={() => handleSort('erp_id')} style={{ cursor: 'pointer' }}>
                                                            ERP ID {getSortIcon('erp_id')}
                                                        </th>
                                                        <th onClick={() => handleSort('tax_id')} style={{ cursor: 'pointer' }}>
                                                            Tax ID {getSortIcon('tax_id')}
                                                        </th>
                                                        <th onClick={() => handleSort('is_default')} style={{ cursor: 'pointer' }}>
                                                            Default {getSortIcon('is_default')}
                                                        </th>
                                                        <th onClick={() => handleSort('last_updated_at')} style={{ cursor: 'pointer' }}>
                                                            Last Updated {getSortIcon('last_updated_at')}
                                                        </th>
                                                        <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                                            Created {getSortIcon('created_at')}
                                                        </th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getFilteredAndSortedFiscalEntities().map((fe) => (
                                                        <tr key={fe.id}>
                                                            <td className="fw-medium">{fe.name || '-'}</td>
                                                            <td><code className="text-primary">{fe.erp_id || '-'}</code></td>
                                                            <td><small>{fe.tax_id || '-'}</small></td>
                                                            <td>
                                                                {fe.is_default ? (
                                                                    <Badge bg="success">Default</Badge>
                                                                ) : (
                                                                    <Badge bg="secondary">No</Badge>
                                                                )}
                                                            </td>
                                                            <td><small>{apiService.formatDate(fe.last_updated_at)}</small></td>
                                                            <td><small>{apiService.formatDate(fe.created_at)}</small></td>
                                                            <td>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    title="Edit"
                                                                    onClick={() => handleShowModal(fe)}
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingFiscalEntity ? 'Edit Fiscal Entity' : 'Add Fiscal Entity'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError('')}>
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {error}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Organization</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedOrganization?.name || ''}
                                disabled
                                readOnly
                            />
                            <Form.Text className="text-muted">
                                Fiscal entity will be linked to this organization
                            </Form.Text>
                        </Form.Group>

                        {!editingFiscalEntity ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Fiscal Entity *</Form.Label>
                                    {loadingErpList ? (
                                        <div className="text-center py-3">
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Loading available fiscal entities...
                                        </div>
                                    ) : (
                                        <>
                                            <Form.Select
                                                value={selectedErpId}
                                                onChange={handleErpSelectChange}
                                                disabled={erpList.length === 0}
                                                required
                                            >
                                                <option value="">-- Select a fiscal entity --</option>
                                                {erpList.map((entry) => (
                                                    <option
                                                        key={entry.code_client}
                                                        value={entry.code_client}
                                                    >
                                                        {entry.ref} (ERP: {entry.code_client}{entry.idprof1 ? `, Tax: ${entry.idprof1}` : ''})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {erpList.length === 0 && (
                                                <Form.Text className="text-danger">
                                                    No fiscal entities available from ERP
                                                </Form.Text>
                                            )}
                                        </>
                                    )}
                                </Form.Group>

                                {selectedErpId && (
                                    <div className="p-3 bg-light rounded mb-3">
                                        <small className="text-muted d-block mb-1">Selected Fiscal Entity:</small>
                                        <div><strong>Name:</strong> {formData.name}</div>
                                        <div><strong>ERP ID:</strong> <code>{formData.erp_id}</code></div>
                                        <div><strong>Tax ID:</strong> {formData.tax_id || '-'}</div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter fiscal entity name"
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>ERP ID *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="erp_id"
                                        value={formData.erp_id}
                                        onChange={handleInputChange}
                                        placeholder="Enter ERP ID"
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        Must be unique across all fiscal entities.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tax ID *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="tax_id"
                                        value={formData.tax_id}
                                        onChange={handleInputChange}
                                        placeholder="Enter Tax ID"
                                        required
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="is_default"
                                checked={formData.is_default}
                                onChange={handleInputChange}
                                label="Default Fiscal Entity"
                            />
                            <Form.Text className="text-muted">
                                Mark this as the default fiscal entity for the organization.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Fiscal Entity'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default FiscalEntities;
